"use client";

import { c as _c } from "react/compiler-runtime";
import { QueryClientProvider, type QueryClient } from "@tanstack/react-query";
import { httpBatchLink, httpSubscriptionLink, loggerLink, splitLink } from "@trpc/client";
import { createTRPCReact } from "@trpc/react-query";
import { type inferRouterInputs, type inferRouterOutputs } from "@trpc/server";
import { useState } from "react";
import SuperJSON from "superjson";
import { type AppRouter } from "~/lib/server/api/root";
import { createQueryClient } from "./query-client";
let clientQueryClientSingleton: QueryClient | undefined = undefined;
const getQueryClient = () => {
  if (typeof window === "undefined") {
    // Server: always make a new query client
    return createQueryClient();
  }
  // Browser: use singleton pattern to keep the same query client
  return clientQueryClientSingleton ??= createQueryClient();
};
export const api = createTRPCReact<AppRouter>();

/**
 * Inference helper for inputs.
 *
 * @example type HelloInput = RouterInputs['example']['hello']
 */
export type RouterInputs = inferRouterInputs<AppRouter>;

/**
 * Inference helper for outputs.
 *
 * @example type HelloOutput = RouterOutputs['example']['hello']
 */
export type RouterOutputs = inferRouterOutputs<AppRouter>;
export function TRPCReactProvider(props) {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = getQueryClient();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const queryClient = t0;
  const [trpcClient] = useState(_temp4);
  let t1;
  if ($[1] !== props.children || $[2] !== trpcClient) {
    t1 = <QueryClientProvider client={queryClient}><api.Provider client={trpcClient} queryClient={queryClient}>{props.children}</api.Provider></QueryClientProvider>;
    $[1] = props.children;
    $[2] = trpcClient;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
function _temp4() {
  return api.createClient({
    links: [loggerLink({
      enabled: _temp
    }), splitLink({
      condition: _temp2,
      true: httpSubscriptionLink({
        transformer: SuperJSON,
        url: getBaseUrl() + "/api/trpc"
      }),
      false: httpBatchLink({
        transformer: SuperJSON,
        url: getBaseUrl() + "/api/trpc",
        headers: _temp3
      })
    })]
  });
}
function _temp3() {
  const headers = new Headers();
  headers.set("x-trpc-source", "nextjs-react");
  return headers;
}
function _temp2(op_0) {
  return op_0.type === "subscription";
}
function _temp(op) {
  return process.env.NODE_ENV === "development" || op.direction === "down" && op.result instanceof Error;
}
function getBaseUrl() {
  if (typeof window !== "undefined") return window.location.origin;
  if (process.env.VERCEL_URL) return `https://${process.env.VERCEL_URL}`;
  return `http://localhost:${process.env.PORT ?? 3000}`;
}